import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { DocumenteEdocs } from '@/ui/documente-edocs'
import { Badge, Card, EmptyState, Skeleton } from '@/ui/primitives'
import { lei } from '@/lib/money'

type Partener = {
  id: number; name: string; supplier_customer: string | null; type: string | null
  city: string | null; county: string | null; country: string | null
  phone: string | null; mobile: string | null; email: string | null
  vat_no: string | null; registration_number: string | null; address: string | null
  total_sales?: number; total_purchases?: number; total_reservations?: number
  is_inactive?: boolean; remarks?: string | null
}

export function PartnerDetail() {
  const { id } = useParams()
  const qc = useQueryClient()
  /** `/partners/{id}` NU intoarce totalurile (verificat: lipsesc total_sales,
   *  total_purchases, total_reservations) — ele exista doar in lista. Le luam din
   *  cache-ul listei daca omul a trecut pe acolo; altfel nu inventam cifre, ascundem cardul. */
  const dinLista = (qc.getQueryData(['parteneri']) as { partners: Partener[] } | undefined)
    ?.partners.find((x) => String(x.id) === id)
  const { data, isPending, isError } = useQuery({
    queryKey: ['partener', id],
    queryFn: () => api.get<Partener>(`/partners/${id}`),
  })

  if (isPending) return (<><PageHeader title="Partener" /><div className="px-8 py-6"><Card><Skeleton rows={5} /></Card></div></>)
  if (isError || !data?.id) return (<><PageHeader title="Partener" /><div className="px-8 py-6"><Card><EmptyState title="Nu am gasit partenerul" hint={`Nu exista un partener cu id ${id}.`} /></Card></div></>)

  const p = data
  const rol = (p.supplier_customer ?? '')
  const camp = (e: string, v: string | null | undefined) => v ? (
    <div><p className="text-[12.5px] text-muted">{e}</p><p className="text-[14.5px]">{v}</p></div>
  ) : null

  return (
    <>
      <PageHeader
        title={p.name}
        count={p.type === 'Individual' ? 'persoana fizica' : p.type === 'Company' ? 'companie' : undefined}
        actions={<Link to="/contacte" className="text-[14px] text-muted hover:text-ink">← Toate contactele</Link>}
      />
      <div className="px-8 py-6 grid gap-6 lg:grid-cols-[1fr_300px] items-start">
        <Card className="p-5">
          <div className="flex gap-2 mb-4">
            <Badge tone={rol.includes('Supplier') ? 'warn' : 'neutral'}>
              {rol === 'Supplier and Customer' ? 'client si furnizor'
                : rol.includes('Supplier') ? 'furnizor' : rol.includes('Customer') ? 'client' : '—'}
            </Badge>
            {p.is_inactive && <Badge tone="bad">inactiv</Badge>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {camp('Telefon', p.phone ?? p.mobile)}
            {camp('Email', p.email)}
            {camp('CUI / CNP', p.vat_no)}
            {camp('Nr. registru', p.registration_number)}
            {camp('Adresa', p.address)}
            {camp('Localitate', [p.city, p.county, p.country].filter(Boolean).join(', ') || null)}
          </div>
          {p.remarks && <p className="mt-4 pt-4 border-t border-line text-[13.5px] text-muted italic">{p.remarks}</p>}
        </Card>

        {dinLista && (
          <Card className="p-5 space-y-3">
            <div className="flex justify-between"><span className="text-[13px] text-muted">Vanzari</span>
              <span className="tnum">{lei(dinLista.total_sales ?? 0)} <span className="text-[11.5px] text-muted">lei</span></span></div>
            <div className="flex justify-between"><span className="text-[13px] text-muted">Cumparari</span>
              <span className="tnum">{lei(dinLista.total_purchases ?? 0)} <span className="text-[11.5px] text-muted">lei</span></span></div>
            <div className="flex justify-between pt-3 border-t border-line"><span className="text-[13px] text-muted">Rezervari</span>
              <span className="tnum">{dinLista.total_reservations ?? 0}</span></div>
          </Card>
        )}

        <DocumenteEdocs tip="partner" id={p.id} />
      </div>
    </>
  )
}
